import { forwardRef, PropsWithChildren } from 'react';

import { ActivityListItem as ActivityListItemType } from '@data';
import { ActionButton, Box, Heading3, Inline, Spinner, Stack, Text, u } from '@ui';

import { ActivityListItem, ActivityListItemProps } from './components/ActivityListItem';

export type ActivitiesListProps = PropsWithChildren<
    {
        activities: ActivityListItemType[] | undefined;
        showNoResultsMessage: boolean;
        isLoading: boolean;
        currentActivityId: number;
        hasNextPage: boolean;
        lastSelectedActivityId: number;
        persistLastSelectedActivityId: (id: number) => void;
        fetchNextPage: () => void;
        retry: () => void;
    } & Pick<ActivityListItemProps, 'getActivityLink'>
>;

export const ActivityList = forwardRef<HTMLDivElement, ActivitiesListProps>(
    (
        {
            activities,
            showNoResultsMessage,
            isLoading,
            currentActivityId,
            lastSelectedActivityId,
            children,
            persistLastSelectedActivityId,
            getActivityLink,
            retry,
        },
        containerRef,
    ) => {
        return (
            <Box ref={containerRef} display="flex" flexDirection="column" gap={8} alignItems="center" flex={1} pb={32}>
                {!isLoading && showNoResultsMessage && (
                    <Stack alignSelf="start" gap={u(2)}>
                        <Inline alignItems="center" gap={u(2)}>
                            <Heading3>No activities match your filters</Heading3>
                            <ActionButton icon="repeat" variant="plain" onClick={retry} />
                        </Inline>
                        <Text>Try adjusting the filters or search terms, or press Retry to load results again.</Text>
                    </Stack>
                )}

                {activities?.map((activity) => (
                    <ActivityListItem
                        key={activity.id}
                        currentActivityId={currentActivityId}
                        lastSelectedActivityId={lastSelectedActivityId}
                        activityId={activity.id}
                        startTime={activity.startTime}
                        tags={activity.tags}
                        starredSwingsCount={activity.favoriteSwingsCount ?? 0}
                        noOfSwings={activity.swingCount ?? 0}
                        clubShortName={activity.club?.shortName}
                        ownerId={activity.userId}
                        getActivityLink={getActivityLink}
                        hasLiveMeasurements={!!activity.lastLiveMeasurementAt}
                        onSelect={() => persistLastSelectedActivityId(activity.id)}
                    />
                ))}

                {isLoading && (
                    <Box display="flex" flex={1} justifyContent="center" alignItems="center" pt={32}>
                        <Spinner size="l" />
                    </Box>
                )}
                {children}
            </Box>
        );
    },
);
